import React, { useState, useContext, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { PayPalButtons, usePayPalScriptReducer } from '@paypal/react-paypal-js';
import { loadStripe } from '@stripe/stripe-js';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { useUser } from '../context/UserContext';
import { GatewayContext } from '../App';
import api from '../api/api';
import { useCurrency } from '../context/CurrencyContext';
import './Cart.css';

function PayPalLoader() {
  const [{ isPending }] = usePayPalScriptReducer();
  if (!isPending) return null;
  return <div className="paypal-loading">Loading PayPal...</div>;
}

function StripeForm({ onSuccess, onError }) {
  const stripe = useStripe();
  const elements = useElements();
  const [loading, setLoading] = useState(false);
  const [ready, setReady] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!stripe || !elements || !ready) return;
    setLoading(true);
    const { error, paymentIntent } = await stripe.confirmPayment({
      elements,
      confirmParams: { return_url: window.location.origin + '/rental-booking' },
      redirect: 'if_required',
    });
    setLoading(false);
    if (error) onError(error.message);
    else if (paymentIntent?.status === 'succeeded') {
      onSuccess({ paymentId: paymentIntent.id, amount: (paymentIntent.amount / 100).toFixed(2) });
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <PaymentElement onReady={() => setReady(true)} />
      <button type="submit" className="btn checkout-btn" disabled={!stripe || loading || !ready} style={{ marginTop: 16, width: '100%' }}>
        {loading ? 'Processing...' : 'Pay Now'}
      </button>
    </form>
  );
}

function RentalBooking() {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, login } = useUser();
  const gateways = useContext(GatewayContext);
  const currency = useCurrency();

  const params = new URLSearchParams(location.search);
  const planName = params.get('plan') || '';
  const planPrice = params.get('price') || '';

  const [step, setStep] = useState(user ? 'details' : 'login');
  const [bookingUser, setBookingUser] = useState(user);
  const [booking, setBooking] = useState(null);
  const [success, setSuccess] = useState(false);
  const [countryCodes, setCountryCodes] = useState([{ code: '+353', name: 'Ireland', flag: 'ie' }]);

  // Login state
  const [loginMode, setLoginMode] = useState('both');
  const [contactType, setContactType] = useState('mobile');
  const [countryCode, setCountryCode] = useState('+353');
  const [mobile, setMobile] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState(['', '', '', '']);
  const [name, setName] = useState('');
  const [isNew, setIsNew] = useState(false);
  const [subStep, setSubStep] = useState('contact');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Load country codes and login mode from admin
  useEffect(() => {
    api.get('/content/country_codes').then(({ data }) => {
      if (data?.codes?.length) {
        setCountryCodes(data.codes);
        setCountryCode(data.defaultCode || data.codes[0]?.code || '+353');
      }
    }).catch(() => {});
    api.get('/auth-settings').then(({ data }) => {
      const mode = data?.loginMode || 'both';
      setLoginMode(mode);
      if (mode === 'email_only') setContactType('email');
      else if (mode === 'sms_only') setContactType('mobile');
    }).catch(() => {});
  }, []);

  // Booking details
  const [bike, setBike] = useState('');
  const [startDate, setStartDate] = useState('');
  const [bookingMobile, setBookingMobile] = useState('');
  const [bookingAddress, setBookingAddress] = useState('');
  const [fleetOptions, setFleetOptions] = useState([]);

  // Load fleet bikes from admin
  useEffect(() => {
    api.get('/services').then(({ data }) => {
      if (data?.fleet?.length) setFleetOptions(data.fleet);
    }).catch(() => {});
  }, []);

  // Payment
  const [payError, setPayError] = useState('');
  const [selectedGateway, setSelectedGateway] = useState(null);
  const [stripePromise, setStripePromise] = useState(null);
  const [stripeClientSecret, setStripeClientSecret] = useState('');

  const paypalEnabled = gateways?.paypal?.enabled;
  const stripeEnabled = gateways?.stripe?.enabled;

  const otpRefs = [React.useRef(), React.useRef(), React.useRef(), React.useRef()];

  // --- LOGIN ---
  const handleSendOtp = async (e) => {
    e.preventDefault();
    setError(''); setLoading(true);
    try {
      const fullMobile = countryCode + mobile;
      const payload = contactType === 'mobile' ? { mobile: fullMobile } : { email };
      const { data } = await api.post('/user/send-otp', payload);
      setIsNew(data.isNew);
      setSubStep('otp');
      setTimeout(() => otpRefs[0].current?.focus(), 100);
    } catch (err) { setError(err.response?.data?.error || 'Failed to send OTP.'); }
    setLoading(false);
  };

  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    const code = otp.join('');
    if (code.length < 4) { setError('Enter the full 4-digit code.'); return; }
    setError(''); setLoading(true);
    try {
      const fullMobile = countryCode + mobile;
      const payload = contactType === 'mobile'
        ? { mobile: fullMobile, otp: code, name: isNew ? name : undefined }
        : { email, otp: code, name: isNew ? name : undefined };
      if (isNew && !name) { setSubStep('name'); setLoading(false); return; }
      const { data } = await api.post('/user/verify-otp', payload);
      login(data.user);
      setBookingUser(data.user);
      setStep('details');
      setBookingMobile(data.user.mobile || '');
    } catch (err) { setError(err.response?.data?.error || 'Invalid code.'); }
    setLoading(false);
  };

  const handleNameSubmit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { setError('Enter your name.'); return; }
    setError(''); setLoading(true);
    try {
      const fullMobile = countryCode + mobile;
      const code = otp.join('');
      const payload = contactType === 'mobile' ? { mobile: fullMobile, otp: code, name } : { email, otp: code, name };
      const { data } = await api.post('/user/verify-otp', payload);
      login(data.user);
      setBookingUser(data.user);
      setStep('details');
      setBookingMobile(data.user.mobile || '');
    } catch (err) { setError(err.response?.data?.error || 'Something went wrong.'); }
    setLoading(false);
  };

  // --- CREATE BOOKING (pending) ---
  const handleCreateBooking = async (e) => {
    e.preventDefault();
    if (!startDate) { setError('Select a start date.'); return; }
    setError(''); setLoading(true);
    try {
      if (!bookingMobile.trim()) { setError('Mobile number is required.'); setLoading(false); return; }
      if (!bookingAddress.trim()) { setError('Address is required.'); setLoading(false); return; }
      const { data } = await api.post('/rental-bookings', {
        customer: { name: bookingUser.name, email: bookingUser.email, mobile: bookingMobile, address: bookingAddress },
        plan: planName,
        bike,
        startDate,
        amount: planPrice.replace(/[^0-9.]/g, ''),
        currency: 'EUR',
        status: 'pending',
      });
      setBooking(data);
      setStep('payment');
    } catch (err) { setError(err.response?.data?.error || 'Failed to create booking.'); }
    setLoading(false);
  };

  // --- PAYMENT ---
  const handleSelectGateway = async (gw) => {
    setSelectedGateway(gw);
    setPayError('');
    if (gw === 'stripe') {
      try {
        const amount = planPrice.replace(/[^0-9.]/g, '') || '0';
        const { data } = await api.post('/stripe/create-payment-intent', { total: amount });
        setStripePromise(loadStripe(gateways.stripe?.publishableKey));
        setStripeClientSecret(data.clientSecret);
      } catch (e) { setPayError(e.response?.data?.error || 'Failed to initialize payment.'); setSelectedGateway(null); }
    }
  };

  const handlePaymentSuccess = async (paymentData) => {
    try {
      await api.put(`/admin/rental-bookings/${booking._id}`, {
        status: 'confirmed',
        gateway: selectedGateway,
        paymentId: paymentData.paymentId || paymentData.orderID || '',
      });
      setSuccess(true);
    } catch { setSuccess(true); }
  };

  // --- RENDER ---
  if (success) {
    return (
      <div className="cart-page section">
        <div className="container checkout-container">
          <div className="order-success">
            <div className="order-success-icon">✅</div>
            <h2>Booking Confirmed!</h2>
            <p>Your rental has been booked successfully.</p>
            <div className="order-success-details">
              <div className="order-detail-row"><span>Booking ID</span><strong>{booking?.bookingID}</strong></div>
              <div className="order-detail-row"><span>Plan</span><strong>{planName}</strong></div>
              <div className="order-detail-row"><span>Amount</span><strong>{currency}{planPrice.replace(/[^0-9.]/g, '')}</strong></div>
              <div className="order-detail-row"><span>Start Date</span><strong>{startDate}</strong></div>
            </div>
            <p className="order-success-note">We'll contact you to confirm pickup details. 🚲</p>
            <button className="btn" onClick={() => navigate('/services')}>Back to Rentals</button>
          </div>
        </div>
      </div>
    );
  }

  // LOGIN STEP
  if (step === 'login') {
    return (
      <div className="cart-page section">
        <div className="container checkout-container">
          <div className="checkout-step">
            <h2>Sign In to Book</h2>
            <p className="checkout-step-sub">Booking: <strong>{planName}</strong> — {planPrice}</p>

            {subStep === 'contact' && (
              <form onSubmit={handleSendOtp} className="checkout-form">
                {loginMode === 'both' && (
                <div style={{ display: 'flex', gap: 8, marginBottom: 12, background: '#f5f5f5', padding: 4, borderRadius: 8 }}>
                  <button type="button" onClick={() => setContactType('mobile')} style={{ flex: 1, padding: 10, border: 'none', borderRadius: 6, background: contactType === 'mobile' ? '#fff' : 'transparent', fontWeight: contactType === 'mobile' ? 600 : 400, cursor: 'pointer' }}>📱 Mobile</button>
                  <button type="button" onClick={() => setContactType('email')} style={{ flex: 1, padding: 10, border: 'none', borderRadius: 6, background: contactType === 'email' ? '#fff' : 'transparent', fontWeight: contactType === 'email' ? 600 : 400, cursor: 'pointer' }}>✉️ Email</button>
                </div>
                )}
                {contactType === 'mobile' ? (
                  <div className="checkout-field">
                    <label>Mobile Number</label>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <div style={{ position: 'relative', width: 130 }}>
                        <select value={countryCode} onChange={e => setCountryCode(e.target.value)} style={{ width: '100%', height: 44, border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14, background: '#fff', cursor: 'pointer', opacity: 0, position: 'absolute', inset: 0, zIndex: 2 }}>
                          {countryCodes.map((c, i) => <option key={i} value={c.code}>{c.name} {c.code}</option>)}
                        </select>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '0 24px 0 10px', height: 44, border: '1.5px solid #ddd', borderRadius: 8, background: '#fff', fontSize: 14, fontWeight: 500, pointerEvents: 'none' }}>
                          <img src={`https://flagcdn.com/24x18/${(countryCodes.find(c => c.code === countryCode)?.flag) || 'ie'}.png`} alt="" style={{ width: 20, height: 15, objectFit: 'cover', borderRadius: 2 }} />
                          <span>{countryCode}</span>
                          <span style={{ marginLeft: 'auto', fontSize: 10, color: '#888' }}>▼</span>
                        </div>
                      </div>
                      <input type="tel" placeholder="87 123 4567" value={mobile} onChange={e => setMobile(e.target.value.replace(/^0+/, ''))} style={{ flex: 1 }} required autoFocus />
                    </div>
                  </div>
                ) : (
                  <div className="checkout-field">
                    <label>Email</label>
                    <input type="email" placeholder="your@email.com" value={email} onChange={e => setEmail(e.target.value)} required autoFocus />
                  </div>
                )}
                {error && <p className="checkout-error">{error}</p>}
                <button type="submit" className="btn checkout-btn" disabled={loading}>{loading ? 'Sending...' : 'Send Login Code'}</button>
              </form>
            )}

            {subStep === 'otp' && (
              <form onSubmit={handleVerifyOtp} className="checkout-form">
                <p className="checkout-step-sub">We sent a 4-digit code to <strong>{contactType === 'mobile' ? countryCode + mobile : email}</strong></p>
                <div className="otp-inputs">
                  {otp.map((d, i) => (
                    <input key={i} ref={otpRefs[i]} type="text" inputMode="numeric" maxLength={1} value={d} className="otp-box"
                      onChange={e => { const v = e.target.value.replace(/\D/g,''); const next = [...otp]; next[i] = v.slice(-1); setOtp(next); if (v && i < 3) otpRefs[i+1].current?.focus(); }}
                      onKeyDown={e => { if (e.key === 'Backspace' && !otp[i] && i > 0) otpRefs[i-1].current?.focus(); }}
                    />
                  ))}
                </div>
                {error && <p className="checkout-error">{error}</p>}
                <button type="submit" className="btn checkout-btn" disabled={loading || otp.join('').length < 4}>{loading ? 'Verifying...' : 'Verify Code'}</button>
              </form>
            )}

            {subStep === 'name' && (
              <form onSubmit={handleNameSubmit} className="checkout-form">
                <div className="checkout-field">
                  <label>Your Name</label>
                  <input placeholder="Full name" value={name} onChange={e => setName(e.target.value)} required autoFocus />
                </div>
                {error && <p className="checkout-error">{error}</p>}
                <button type="submit" className="btn checkout-btn" disabled={loading}>{loading ? 'Please wait...' : 'Continue'}</button>
              </form>
            )}
          </div>
        </div>
      </div>
    );
  }

  // DETAILS STEP
  if (step === 'details') {
    return (
      <div className="cart-page section">
        <div className="container checkout-container">
          <div className="checkout-step">
            <h2>Booking Details</h2>
            <p className="checkout-step-sub">Plan: <strong>{planName}</strong> — {planPrice}</p>
            <form onSubmit={handleCreateBooking} className="checkout-form">
              <div className="checkout-field">
                <label>Mobile Number</label>
                <input type="tel" placeholder="e.g. +353 87 123 4567" value={bookingMobile} onChange={e => setBookingMobile(e.target.value)} required />
              </div>
              <div className="checkout-field">
                <label>Address</label>
                <textarea placeholder="Your full address" value={bookingAddress} onChange={e => setBookingAddress(e.target.value)} required rows={3} style={{ resize: 'vertical' }} />
              </div>
              <div className="checkout-field">
                <label>Start Date</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} required min={new Date().toISOString().split('T')[0]} />
              </div>
              <div className="checkout-field">
                <label>Preferred Bike</label>
                {fleetOptions.length > 0 ? (
                  <select value={bike} onChange={e => setBike(e.target.value)} style={{ padding: '11px 14px', border: '1.5px solid #ddd', borderRadius: 8, fontSize: 14 }}>
                    <option value="">— Select a bike —</option>
                    {fleetOptions.map((b, i) => <option key={i} value={b.name}>{b.name}{b.specs ? ` (${b.specs})` : ''}</option>)}
                  </select>
                ) : (
                  <input placeholder="e.g. City Commuter E-Bike" value={bike} onChange={e => setBike(e.target.value)} required />
                )}
              </div>
              <div style={{ background: '#f8f9fa', borderRadius: 8, padding: 14, fontSize: 13, color: '#555' }}>
                <strong>Customer:</strong> {bookingUser?.name} · {bookingUser?.mobile || bookingUser?.email}
              </div>
              {error && <p className="checkout-error">{error}</p>}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 12, marginTop: 8 }}>
                <div style={{ display: 'flex', gap: 12 }}>
                <button type="submit" className="rental-btn rental-btn-fill" disabled={loading}>{loading ? 'Processing...' : '💳 Continue to Payment'}</button>
              <button type="button" className="rental-btn rental-btn-outline-orange" disabled={loading} onClick={async (e) => {
                const form = e.target.closest('form');
                if (form && !form.reportValidity()) return;
                setLoading(true); setError('');
                try {
                  const { data } = await api.post('/rental-bookings', {
                    userId: bookingUser._id,
                    name: bookingUser.name,
                    mobile: bookingMobile,
                    address: bookingAddress,
                    startDate,
                    bike,
                    plan: planName,
                    amount: planPrice.replace(/[^0-9.]/g, ''),
                    currency: 'EUR',
                    status: 'pending',
                    paymentMethod: 'pay_at_pickup',
                  });
                  setBooking(data);
                  setSuccess(true);
                } catch (err) { setError(err.response?.data?.error || 'Failed to create booking.'); }
                setLoading(false);
              }}>🏧 Pay at Pickup</button>
              </div>
                <button type="button" className="rental-btn rental-btn-outline" onClick={() => navigate('/services')}>← Cancel</button>
              </div>
            </form>
          </div>
        </div>
      </div>
    );
  }

  // PAYMENT STEP
  if (step === 'payment') {
    const amount = planPrice.replace(/[^0-9.]/g, '') || '0';
    return (
      <div className="cart-page section">
        <div className="container checkout-container">
          <div className="checkout-step">
            <h2>Payment</h2>
            <div className="checkout-order-summary">
              <div className="checkout-order-item"><span>{planName} Rental</span><span>{currency}{amount}</span></div>
              <div className="checkout-order-total"><strong>Total</strong><strong>{currency}{amount}</strong></div>
            </div>
            <p style={{ fontSize: 12, color: '#888', margin: '0 0 16px' }}>Booking ID: {booking?.bookingID}</p>

            <div className="payment-options">
              {paypalEnabled && (
                <div className={`payment-option${selectedGateway === 'paypal' ? ' selected' : ''}`} onClick={() => handleSelectGateway('paypal')}>
                  <div className="payment-option-row">
                    <span className="payment-radio"><span className={`payment-radio-dot${selectedGateway === 'paypal' ? ' active' : ''}`} /></span>
                    <span className="payment-option-label">PayPal</span>
                  </div>
                  {selectedGateway === 'paypal' && (
                    <div className="payment-option-body">
                      <PayPalLoader />
                      <PayPalButtons
                        style={{ layout: 'vertical', shape: 'rect', label: 'pay', height: 48 }}
                        createOrder={async () => {
                          const { data } = await api.post('/paypal/create-order', { items: [{ name: planName, price: amount, qty: 1 }], total: amount });
                          return data.id;
                        }}
                        onApprove={async (d) => {
                          const { data } = await api.post(`/paypal/capture-order/${d.orderID}`);
                          await handlePaymentSuccess({ paymentId: data.orderID, orderID: data.orderID });
                        }}
                        onError={() => setPayError('Payment failed.')}
                        onCancel={() => setPayError('Payment cancelled.')}
                      />
                    </div>
                  )}
                </div>
              )}
              {stripeEnabled && (
                <div className={`payment-option${selectedGateway === 'stripe' ? ' selected' : ''}`} onClick={() => handleSelectGateway('stripe')}>
                  <div className="payment-option-row">
                    <span className="payment-radio"><span className={`payment-radio-dot${selectedGateway === 'stripe' ? ' active' : ''}`} /></span>
                    <span className="payment-option-label">Credit / Debit Card</span>
                  </div>
                  {selectedGateway === 'stripe' && stripeClientSecret && stripePromise && (
                    <div className="payment-option-body">
                      <Elements stripe={stripePromise} options={{ clientSecret: stripeClientSecret }}>
                        <StripeForm onSuccess={(r) => handlePaymentSuccess({ paymentId: r.paymentId || '' })} onError={msg => setPayError(msg)} />
                      </Elements>
                    </div>
                  )}
                </div>
              )}
            </div>

            {payError && <div className="paypal-error" style={{ marginTop: 12 }}>{payError}</div>}

            <button className="rental-btn rental-btn-outline" style={{ marginTop: 16 }} onClick={() => setStep('details')}>← Back</button>
          </div>
        </div>
      </div>
    );
  }

  return null;
}

export default RentalBooking;
